import { useCallback, useEffect } from "react";
import queryKeys from "@configs/react-query/query-key";
import { LOCAL_STORAGE } from "@constants/index";
import { API } from "@apis/index";
import { forceLogoutByAccountInactive, forceLogoutByAuthorizationChange } from "@utils/auth";

export const normalizePermissions = (permissions) => {
  if (!Array.isArray(permissions)) {
    return [];
  }
  return [...permissions].map((value) => String(value)).sort();
};

export const arePermissionsEqual = (left, right) => {
  const normalizedLeft = normalizePermissions(left);
  const normalizedRight = normalizePermissions(right);
  return JSON.stringify(normalizedLeft) === JSON.stringify(normalizedRight);
};

export const isProfileActive = (profile) => {
  if (!profile) {
    return true;
  }
  if (typeof profile.active === "boolean") {
    return profile.active;
  }
  if (profile.enableFlag === "N") {
    return false;
  }
  return true;
};

const readStoredPermissions = () => {
  const rawPermissions = localStorage.getItem(LOCAL_STORAGE.PERMISSIONS);
  if (!rawPermissions) {
    return null;
  }
  try {
    const parsed = JSON.parse(rawPermissions);
    return Array.isArray(parsed) ? parsed : null;
  } catch (error) {
    return null;
  }
};

const useAuthorizationWatcher = (clearPermissions) => {
  const verifyPermissionsOnStartup = useCallback(async () => {
    if (!localStorage.getItem(LOCAL_STORAGE.TOKEN)) {
      return;
    }
    try {
      const response = await API.get(queryKeys.getMyProfile, { skipErrorToast: true });
      const profile = response?.data;
      if (!isProfileActive(profile)) {
        await forceLogoutByAccountInactive(clearPermissions);
        return;
      }
      const latestPermissions = profile?.permissions || [];
      const storedPermissions = readStoredPermissions();

      if (storedPermissions === null) {
        localStorage.setItem(LOCAL_STORAGE.PERMISSIONS, JSON.stringify(latestPermissions));
        return;
      }

      if (!arePermissionsEqual(storedPermissions, latestPermissions)) {
        await forceLogoutByAuthorizationChange(clearPermissions);
      }
    } catch (error) {
      console.warn("Failed to verify permissions on startup:", error);
    }
  }, [clearPermissions]);

  useEffect(() => {
    verifyPermissionsOnStartup();
  }, [verifyPermissionsOnStartup]);
};

export default useAuthorizationWatcher;
